/**
 * Esecuzione di un test di misura (V73): nuova, su `/sq/:ss/test/:id/esegui`,
 * o da correggere, su `/sq/:ss/test/:id/esecuzioni/:esecuzioneId`.
 *
 * Si sceglie la data e si scrivono i risultati della rosa, portieri compresi, un
 * giocatore per riga e un gruppo di colonne per misura; il punteggio compare
 * mentre si scrive e Invio porta alla cella sotto, per inserire in fila tutta
 * la rosa. Una misura lasciata vuota non si salva, e chi non ne ha nessuna non
 * ha fatto il test. Il punteggio salvato lo calcola il server, che scrive le
 * misurazioni. Quando le colonne non ci stanno, con più misure se ne vede una
 * alla volta, scelta dai Tabs sopra la tabella.
 *
 * In correzione valgono le misure copiate nell'esecuzione, e i giocatori già
 * salvati restano anche se nel frattempo hanno chiuso la tessera. L'Admin apre
 * un'esecuzione e la legge soltanto.
 *
 * Da un blocco di seduta (`?blocco=`, o un'esecuzione già legata a un blocco)
 * la data è quella della seduta e non si cambia, le righe sono i presenti dei
 * gruppi del blocco e indietro si torna alla seduta. Se il blocco ha già la
 * sua esecuzione, si apre quella.
 */

import { Fragment, type ReactNode, useMemo, useState } from 'react';
import { Navigate, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { ETICHETTA_METODO_TEST, puo, type ParametriTest } from '@calcio/shared';
import { CalculatorIcon, ClipboardPenLineIcon, OctagonXIcon, Trash2Icon } from 'lucide-react';
import { toast } from 'sonner';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Badge } from '@/components/ui/badge';
import { Field, FieldLabel } from '@/components/ui/field';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { MenuAzioni, PulsanteInvio } from '../comuni/Azioni.js';
import { chiama } from '../api/client.js';
import { Corpo } from '../comuni/Corpo.js';
import { dataBreve, messaggio, useDati } from '../comuni/dati.js';
import { oggi } from '../comuni/date.js';
import { StatoCaricamento, Testata } from '../comuni/Testata.js';
import { useContesto } from '../navigazione/contesto.js';
import { NonTrovata } from '../navigazione/NonTrovata.js';
import { CampoData } from '../comuni/CampoData.js';
import { CampoNumero } from './CampoNumero.js';
import { CardSezione } from './CardSezione.js';
import { IconaMetodi } from './icone.js';
import {
  giocatoriDellaVista,
  misureDaEseguire,
  misureDellEsecuzione,
  righeCompilate,
  righeDaSalvare,
  risultatoAParole,
  valoriDaEsecuzione,
  valutaRiga,
  type EsecuzioneDettaglio,
  type Idonei,
  type MisuraDellaVista,
  type ValoriEsecuzione,
} from './esecuzione.js';
import {
  CAMPI_RISULTATO,
  formulaAParole,
  unitaDelCampo,
  type TestDettaglio,
} from './test-misura.js';
import { cn } from '@/lib/utils';
import { Nota } from '../comuni/Nota.js';
import { StatoVuoto } from '../comuni/StatoVuoto.js';

export function EsecuzioneTest() {
  const { ss, id, esecuzioneId } = useParams<{ ss: string; id: string; esecuzioneId?: string }>();
  const { utente } = useContesto();
  const scrive = Boolean(ss && puo(utente.assegnazioni, 'misurazione.scrivi', ss));
  const test = useDati<TestDettaglio>(id ? `/test/${id}` : '');
  const esecuzione = useDati<EsecuzioneDettaglio>(
    esecuzioneId ? `/squadre-stagione/${ss}/esecuzioni-test/${esecuzioneId}` : '',
  );
  const [cerca] = useSearchParams();
  // Dal blocco di seduta: quello nella query, o quello a cui l'esecuzione è legata.
  const bloccoId = cerca.get('blocco') ?? esecuzione.dati?.blocco?.id ?? null;
  // La rosa la legge solo chi scrive: all'Admin basta l'esecuzione salvata. In
  // correzione si aspetta l'esecuzione, per sapere se viene da un blocco.
  const idonei = useDati<Idonei>(
    scrive && (!esecuzioneId || esecuzione.dati)
      ? `/squadre-stagione/${ss}/test/${id}/idonei${bloccoId ? `?blocco=${bloccoId}` : ''}`
      : '',
  );

  if (!ss || !id || (!esecuzioneId && !scrive)) return <NonTrovata />;
  if (esecuzione.stato === 404 || esecuzione.stato === 403) return <NonTrovata />;

  const dettaglio = `/sq/${ss}/test/${id}`;
  const titolo = esecuzioneId ? 'Esecuzione' : 'Esegui test';
  // Una nuova esecuzione senza rosa non si fa: il server dice perché (test disattivato…).
  const errore =
    test.errore ?? esecuzione.errore ?? (!esecuzioneId && scrive ? idonei.errore : null);
  const pronti =
    test.dati && (!esecuzioneId || esecuzione.dati) && (!scrive || idonei.dati || idonei.errore);
  const giaSalvata = !esecuzioneId && idonei.dati?.blocco?.esecuzioneId;
  if (giaSalvata) return <Navigate to={`${dettaglio}/esecuzioni/${giaSalvata}`} replace />;
  if (errore || !pronti) {
    return (
      <>
        <Testata titolo={titolo} indietro={dettaglio} />
        <Corpo>
          <StatoCaricamento errore={errore} />
        </Corpo>
      </>
    );
  }

  return (
    <Esecuzione
      key={esecuzione.dati?.id ?? 'nuova'}
      ss={ss}
      test={test.dati!}
      esecuzione={esecuzione.dati ?? undefined}
      idonei={idonei.dati}
      erroreIdonei={idonei.errore}
      scrive={scrive}
    />
  );
}

function Esecuzione({
  ss,
  test,
  esecuzione,
  idonei,
  erroreIdonei,
  scrive,
}: {
  ss: string;
  test: TestDettaglio;
  esecuzione?: EsecuzioneDettaglio;
  idonei: Idonei | null;
  erroreIdonei: string | null;
  scrive: boolean;
}) {
  const navigate = useNavigate();
  const dettaglio = `/sq/${ss}/test/${test.id}`;
  // Il test fatto in un blocco di seduta: data fissa, presenti, e indietro alla seduta.
  const seduta = idonei?.blocco;
  const legato = seduta ?? esecuzione?.blocco;
  const indietro = legato ? `/sq/${ss}/allenamenti/${legato.allenamentoId}/blocchi` : dettaglio;
  // In correzione valgono le misure copiate nell'esecuzione, non quelle di oggi.
  const misure = useMemo(
    () => (esecuzione ? misureDellEsecuzione(esecuzione) : misureDaEseguire(test)),
    [esecuzione, test],
  );
  const giocatori = useMemo(
    () => giocatoriDellaVista(idonei?.giocatori ?? [], esecuzione?.righe ?? []),
    [idonei, esecuzione],
  );

  const [data, setData] = useState(seduta?.data ?? esecuzione?.data ?? oggi());
  const [valori, setValori] = useState<ValoriEsecuzione>(() =>
    esecuzione ? valoriDaEsecuzione(esecuzione) : {},
  );
  const [scelta, setScelta] = useState(misure[0]?.chiave ?? '');
  const [tentato, setTentato] = useState(false);
  const [erroreServer, setErroreServer] = useState<string | null>(null);
  const [inCorso, setInCorso] = useState(false);
  const [eliminaAperta, setEliminaAperta] = useState(false);

  const compilate = righeCompilate(
    misure,
    giocatori.map((g) => g.id),
    valori,
  );
  const metodi = [...new Set(misure.map((m) => m.parametri.metodo))];
  const attributi = misure.flatMap((m) => m.attributi);

  const scrivi = (giocatore: string, misura: string, campo: string, v: string) =>
    setValori((tutti) => ({
      ...tutti,
      [giocatore]: {
        ...tutti[giocatore],
        [misura]: { ...tutti[giocatore]?.[misura], [campo]: v },
      },
    }));

  const salva = async () => {
    const esito = righeDaSalvare(
      misure,
      giocatori.map((g) => g.id),
      valori,
    );
    if ('errore' in esito) {
      setTentato(true);
      setErroreServer(esito.errore);
      return;
    }
    setErroreServer(null);
    setInCorso(true);
    try {
      await chiama(
        esecuzione
          ? `/squadre-stagione/${ss}/esecuzioni-test/${esecuzione.id}`
          : `/squadre-stagione/${ss}/test/${test.id}/esecuzioni${seduta ? `?blocco=${seduta.id}` : ''}`,
        { metodo: esecuzione ? 'PUT' : 'POST', corpo: { data, righe: esito.righe } },
      );
      toast.success('Esecuzione salvata.');
      navigate(indietro, { replace: true });
    } catch (e) {
      setErroreServer(messaggio(e));
      setInCorso(false);
    }
  };

  const elimina = async () => {
    if (!esecuzione) return;
    try {
      await chiama(`/squadre-stagione/${ss}/esecuzioni-test/${esecuzione.id}`, {
        metodo: 'DELETE',
      });
      toast.success('Esecuzione eliminata.');
      navigate(indietro, { replace: true });
    } catch (e) {
      toast.error(messaggio(e));
      setEliminaAperta(false);
    }
  };

  const scegli =
    misure.length > 1 ? (
      <SceltaMisura misure={misure} scelta={scelta} onScegli={setScelta} />
    ) : null;

  return (
    <>
      <Testata
        titolo={test.nome}
        sopra={
          esecuzione
            ? `Esecuzione del ${dataBreve(esecuzione.data)}${legato ? ', in seduta' : ''}`
            : seduta
              ? `Seduta del ${dataBreve(seduta.data)}`
              : 'Nuova esecuzione'
        }
        indietro={indietro}
        azione={
          esecuzione && (
            <MenuAzioni
              voci={[
                {
                  etichetta: 'Elimina esecuzione',
                  Icona: Trash2Icon,
                  onSelect: () => setEliminaAperta(true),
                  distruttiva: true,
                },
              ]}
            />
          )
        }
      >
        <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-sm">
          <span className="flex items-center gap-1 font-medium">
            <IconaMetodi metodi={metodi} className="size-4 text-muted-foreground" />
            {metodi.map((m) => ETICHETTA_METODO_TEST[m]).join(', ')}
          </span>
          <span className="text-muted-foreground">{attributi.map((a) => a.nome).join(', ')}</span>
          {esecuzione?.autore && (
            <span className="text-muted-foreground">Registrata da {esecuzione.autore}</span>
          )}
        </div>
      </Testata>
      <Corpo className="flex flex-col gap-4">
        <CardSezione titolo="Come si calcola" Icona={CalculatorIcon}>
          {misure.length === 1 ? (
            <p className="px-1 text-sm">{formulaAParole(misure[0]!.parametri, misure[0]!.unita)}</p>
          ) : (
            <ul className="flex flex-col gap-1.5 px-1 text-sm">
              {misure.map((m) => (
                <li key={m.chiave}>
                  <span className="font-medium">{m.nome}</span>
                  <span className="text-muted-foreground">
                    {' '}
                    ({m.attributi.map((a) => a.nome).join(', ')})
                  </span>
                  : {formulaAParole(m.parametri, m.unita)}
                </li>
              ))}
            </ul>
          )}
          {test.istruzioni && (
            <p className="rounded-lg border border-border bg-card p-3 text-sm whitespace-pre-line">
              {test.istruzioni}
            </p>
          )}
        </CardSezione>

        {!scrive ? (
          <CardSezione
            titolo="Risultati"
            Icona={ClipboardPenLineIcon}
            conteggio={esecuzione!.righe.length}
          >
            {scegli}
            <Letta esecuzione={esecuzione!} misure={misure} scelta={scelta} />
          </CardSezione>
        ) : (
          <>
            <CardSezione
              titolo="Risultati"
              Icona={ClipboardPenLineIcon}
              conteggio={giocatori.length > 0 ? `${compilate}/${giocatori.length}` : undefined}
            >
              {seduta ? (
                <Nota className="px-1 text-center">
                  Il test del blocco in seduta, il {dataBreve(seduta.data)}: la data è quella della
                  seduta, e lo fanno i presenti dei gruppi del blocco.
                </Nota>
              ) : (
                <Field className="w-auto px-1">
                  <FieldLabel htmlFor="esec-data">Data</FieldLabel>
                  <CampoData id="esec-data" valore={data} onCambia={setData} />
                </Field>
              )}
              {erroreIdonei && (
                <p className="px-1 text-sm text-muted-foreground">
                  {erroreIdonei} Si correggono i giocatori già nell’esecuzione.
                </p>
              )}
              {giocatori.length === 0 ? (
                <StatoVuoto titolo="Nessun giocatore nella rosa" />
              ) : (
                <>
                  <Nota className="px-1 text-center">
                    {misure.length > 1
                      ? 'Una misura lasciata vuota non si salva; chi non ne ha nessuna non ha fatto il test'
                      : 'Chi resta senza risultato non ha fatto il test e non si salva'}
                    {esecuzione ? ': svuotare una riga toglie il giocatore dall’esecuzione' : ''}.
                    Invio passa al giocatore sotto.
                  </Nota>
                  {scegli}
                  <div className="rounded-lg border border-border bg-card">
                    <Table>
                      <Intestazioni misure={misure} scelta={scelta}>
                        {(m) => (
                          <>
                            {CAMPI_RISULTATO[m.parametri.metodo].map((c) => {
                              const u = unitaDelCampo(c, m.unita);
                              return (
                                <TableHead key={c.chiave} className={colonna(m, scelta, misure)}>
                                  {c.etichetta}
                                  {u && ` (${u})`}
                                </TableHead>
                              );
                            })}
                            {nomeDerivato(m.parametri) && (
                              <TableHead className={cn('text-right', colonna(m, scelta, misure))}>
                                {nomeDerivato(m.parametri)}
                              </TableHead>
                            )}
                            <TableHead className={cn('text-right', colonna(m, scelta, misure))}>
                              Punteggio
                            </TableHead>
                          </>
                        )}
                      </Intestazioni>
                      <TableBody>
                        {giocatori.map((g, indice) => (
                          <TableRow key={g.id}>
                            <CellaGiocatore>
                              {g.cognome} {g.nome}
                              {g.fuoriRosa && (
                                <Badge variant="secondary" className="ml-2">
                                  Fuori rosa
                                </Badge>
                              )}
                            </CellaGiocatore>
                            {misure.map((m, mi) => {
                              const v = valori[g.id]?.[m.chiave] ?? {};
                              const esito = valutaRiga(m.parametri, v);
                              const errore = (campo: string) =>
                                esito.stato === 'errore' &&
                                (tentato || (v[campo] ?? '').trim() !== '')
                                  ? esito.errori[campo]
                                  : undefined;
                              const cls = colonna(m, scelta, misure);
                              return (
                                <Fragment key={m.chiave}>
                                  {CAMPI_RISULTATO[m.parametri.metodo].map((c, ci) => (
                                    <TableCell
                                      key={c.chiave}
                                      className={cn(
                                        'align-top',
                                        ci === 0 && mi > 0 && 'border-l',
                                        cls,
                                      )}
                                    >
                                      <div className="w-20 @xl:w-28">
                                        <CampoNumero
                                          id={`esec-${mi}-${c.chiave}-${indice}`}
                                          etichetta={`${m.nome}, ${c.etichetta} di ${g.cognome} ${g.nome}`}
                                          etichettaNascosta
                                          unita={null}
                                          valore={v[c.chiave] ?? ''}
                                          onCambia={(t) => scrivi(g.id, m.chiave, c.chiave, t)}
                                          onKeyDown={sotto(`${mi}-${c.chiave}`, indice)}
                                          errore={errore(c.chiave)}
                                        />
                                      </div>
                                    </TableCell>
                                  ))}
                                  {nomeDerivato(m.parametri) && (
                                    <TableCell className={cn('text-right tabular-nums', cls)}>
                                      {esito.stato === 'piena' && esito.derivato !== null
                                        ? conDerivato(m.parametri, esito.derivato)
                                        : '—'}
                                    </TableCell>
                                  )}
                                  <TableCell
                                    className={cn(
                                      'text-right text-base font-semibold tabular-nums',
                                      cls,
                                    )}
                                  >
                                    {esito.stato === 'piena' ? esito.punteggio : '—'}
                                  </TableCell>
                                </Fragment>
                              );
                            })}
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                </>
              )}
            </CardSezione>

            {erroreServer && (
              <Alert variant="destructive">
                <OctagonXIcon />
                <AlertTitle>Salvataggio non riuscito</AlertTitle>
                <AlertDescription>{erroreServer}</AlertDescription>
              </Alert>
            )}

            <div className="flex justify-end">
              <PulsanteInvio type="button" inCorso={inCorso} onClick={() => void salva()}>
                Salva misurazioni
              </PulsanteInvio>
            </div>
          </>
        )}
      </Corpo>

      {esecuzione && (
        <AlertDialog open={eliminaAperta} onOpenChange={setEliminaAperta}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>
                Eliminare l’esecuzione del {dataBreve(esecuzione.data)}?
              </AlertDialogTitle>
              <AlertDialogDescription>
                Spariscono i risultati e le misurazioni che ha dato a{' '}
                {esecuzione.righe.length === 1
                  ? 'un giocatore'
                  : `${esecuzione.righe.length} giocatori`}
                . La cancellazione resta nel Registro attività.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>Annulla</AlertDialogCancel>
              <AlertDialogAction variant="destructive" onClick={() => void elimina()}>
                Elimina
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      )}
    </>
  );
}

// Invio passa alla stessa colonna del giocatore sotto: i risultati si dettano in fila.
const sotto = (colonna: string, indice: number) => (e: React.KeyboardEvent<HTMLInputElement>) => {
  if (e.key !== 'Enter') return;
  e.preventDefault();
  document.getElementById(`esec-${colonna}-${indice + 1}`)?.focus();
};

const nomeDerivato = (p: ParametriTest) =>
  p.metodo === 'gradini' ? 'Metri' : p.metodo === 'penalita' ? 'Netto' : null;

const conDerivato = (p: ParametriTest, v: number) =>
  `${v.toLocaleString('it-IT')} ${p.metodo === 'gradini' ? 'm' : 's'}`;

/**
 * Le colonne di una misura: con più misure, finché non ci stanno tutte, si
 * vede solo quella scelta nei Tabs.
 */
const colonna = (m: MisuraDellaVista, scelta: string, misure: readonly MisuraDellaVista[]) =>
  misure.length > 1 && m.chiave !== scelta ? 'hidden @3xl:table-cell' : undefined;

/** I Tabs delle misure, solo quando le colonne non ci stanno tutte. */
function SceltaMisura({
  misure,
  scelta,
  onScegli,
}: {
  misure: readonly MisuraDellaVista[];
  scelta: string;
  onScegli: (chiave: string) => void;
}) {
  return (
    <Tabs value={scelta} onValueChange={onScegli} className="@3xl:hidden">
      <TabsList className="w-full">
        {misure.map((m) => (
          <TabsTrigger key={m.chiave} value={m.chiave}>
            {m.nome}
          </TabsTrigger>
        ))}
      </TabsList>
    </Tabs>
  );
}

/** Il giocatore resta fermo a sinistra mentre le misure scorrono. */
function CellaGiocatore({ children }: { children: ReactNode }) {
  return (
    <TableCell className="sticky left-0 z-10 min-w-28 bg-card font-medium whitespace-normal">
      {children}
    </TableCell>
  );
}

/**
 * L'intestazione della tabella: con più misure una riga coi nomi delle misure
 * sopra le colonne di ciascuna.
 */
function Intestazioni({
  misure,
  scelta,
  children,
}: {
  misure: readonly MisuraDellaVista[];
  scelta: string;
  children: (m: MisuraDellaVista) => ReactNode;
}) {
  const giocatore = (righe: number) => (
    <TableHead rowSpan={righe} className="sticky left-0 z-10 bg-card">
      Giocatore
    </TableHead>
  );
  if (misure.length === 1) {
    return (
      <TableHeader>
        <TableRow>
          {giocatore(1)}
          {children(misure[0]!)}
        </TableRow>
      </TableHeader>
    );
  }
  return (
    <TableHeader>
      <TableRow>
        {giocatore(2)}
        {misure.map((m, mi) => (
          <TableHead
            key={m.chiave}
            colSpan={
              CAMPI_RISULTATO[m.parametri.metodo].length + (nomeDerivato(m.parametri) ? 2 : 1)
            }
            className={cn(
              'text-center text-foreground',
              mi > 0 && 'border-l',
              colonna(m, scelta, misure),
            )}
          >
            {m.nome}
          </TableHead>
        ))}
      </TableRow>
      <TableRow>
        {misure.map((m) => (
          <Fragment key={m.chiave}>{children(m)}</Fragment>
        ))}
      </TableRow>
    </TableHeader>
  );
}

/** L'esecuzione in sola lettura, per chi non scrive le misurazioni. */
function Letta({
  esecuzione,
  misure,
  scelta,
}: {
  esecuzione: EsecuzioneDettaglio;
  misure: readonly MisuraDellaVista[];
  scelta: string;
}) {
  return (
    <div className="rounded-lg border border-border bg-card">
      <Table>
        <Intestazioni misure={misure} scelta={scelta}>
          {(m) => (
            <>
              <TableHead className={colonna(m, scelta, misure)}>Risultato</TableHead>
              <TableHead className={cn('text-right', colonna(m, scelta, misure))}>
                Punteggio
              </TableHead>
            </>
          )}
        </Intestazioni>
        <TableBody>
          {esecuzione.righe.map((r) => (
            <TableRow key={r.giocatoreId}>
              <CellaGiocatore>
                {r.cognome} {r.nome}
              </CellaGiocatore>
              {misure.map((m, mi) => {
                const v = r.risultati[m.chiave];
                const cls = colonna(m, scelta, misure);
                return (
                  <Fragment key={m.chiave}>
                    <TableCell className={cn(mi > 0 && 'border-l', cls)}>
                      {v ? risultatoAParole(v.risultato, m.unita, v.derivato) : '—'}
                    </TableCell>
                    <TableCell
                      className={cn('text-right text-base font-semibold tabular-nums', cls)}
                    >
                      {v?.punteggio ?? '—'}
                    </TableCell>
                  </Fragment>
                );
              })}
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  );
}
